import { Box, Card, CardContent, Chip, Stack, Typography } from "@mui/material";
import ExternalLink from "../../../common-components/ExternalLink";
import { DELIVERY_CARRIER_LABELS } from "../../../utility/constants";
import ProofImages from "./ProofImages";

const formatDateTime = (date) =>
  date
    ? new Date(date).toLocaleString(undefined, { timeZoneName: "short" })
    : null;

export const carrierLabel = (tracking) =>
  tracking?.carrier === "OTHER"
    ? tracking.carrierName || "Other"
    : DELIVERY_CARRIER_LABELS[tracking?.carrier] || tracking?.carrier;

const Field = ({ label, children }) => (
  <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
    <Typography component="span" variant="body2" color="text.secondary">
      {label}:{" "}
    </Typography>
    {children}
  </Typography>
);

// Admin view of a DELIVERY order's delivery record: the instructions the buyer
// saw at checkout, the seller's current tracking, their delivery updates and
// any proof-of-delivery photos. Edits to the tracking are in the change log.
const OrderDeliveryTracking = ({ order }) => {
  const delivery = order.delivery;
  if (delivery?.deliveryMethod !== "DELIVERY") return null;
  const tracking = delivery.tracking;
  const updates = [...(delivery.updates || [])].reverse();

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography fontWeight={600} mb={1}>
          Delivery
        </Typography>
        {delivery.instructionsSnapshot && (
          <Box mb={1.5} p={1} bgcolor="grey.100" borderRadius={1}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Seller's delivery instructions at checkout
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {delivery.instructionsSnapshot}
            </Typography>
          </Box>
        )}
        {tracking ? (
          <Stack spacing={0.5} mb={1.5}>
            {tracking.carrier && (
              <Field label="Courier">{carrierLabel(tracking)}</Field>
            )}
            {tracking.trackingNumber && (
              <Field label="Tracking number">{tracking.trackingNumber}</Field>
            )}
            {tracking.trackingUrl && (
              <Field label="Tracking link">
                <ExternalLink
                  value={tracking.trackingUrl}
                  warnUnrecognized={tracking.isRecognizedLink === false}
                />
                {tracking.isRecognizedLink === false && (
                  <Chip
                    size="small"
                    color="warning"
                    label="Not a known courier site"
                    sx={{ ml: 1 }}
                  />
                )}
              </Field>
            )}
            {tracking.driverName && (
              <Field label="Driver">{tracking.driverName}</Field>
            )}
            {tracking.driverPhone && (
              <Field label="Driver phone">{tracking.driverPhone}</Field>
            )}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            No tracking added.
          </Typography>
        )}
        {updates.length > 0 && (
          <Box mb={1.5}>
            <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
              Delivery updates
            </Typography>
            {updates.map((update, i) => (
              <Box
                key={i}
                sx={{ borderLeft: 2, borderColor: "grey.300", pl: 1.5, mb: 1 }}
              >
                <Typography variant="caption" color="text.secondary">
                  {formatDateTime(update.at)}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {update.text}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        <ProofImages
          title="Proof of delivery"
          images={delivery.proofOfDeliveryImages}
        />
      </CardContent>
    </Card>
  );
};

export default OrderDeliveryTracking;
